import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/lib/stores/auth";
import { isFreeUser } from "@/lib/user-type";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LogOutIcon,
  MessageCircleIcon,
  MailIcon,
  ChevronDownIcon,
  SparklesIcon,
} from "lucide-react";

// Conversion CTA for free users only. Lives in the global navbar so it's
// always one click away. Discord-link is hidden for free users (they're not
// in the Lab yet, no Discord access to "join"); paid users keep the Discord
// shortcut in the profile dropdown.
function JoinLabButton() {
  const user = useAuthStore((s) => s.user);
  if (!isFreeUser(user)) return null;

  return (
    <Button asChild size="sm" className="gap-1.5">
      <a
        href="https://irreplaceable.engineer/lab"
        target="_blank"
        rel="noopener noreferrer"
      >
        <SparklesIcon className="size-4" />
        Join Lab
      </a>
    </Button>
  );
}

function UserAvatar() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  if (user.type === "whop" && user.whop_avatar) {
    return (
      <img
        src={user.whop_avatar}
        alt={user.whop_username}
        className="size-9 rounded-full"
      />
    );
  }

  if (user.type === "discord" && user.discord_avatar) {
    return (
      <img
        src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.discord_avatar}.png`}
        alt={user.discord_username}
        className="size-9 rounded-full"
      />
    );
  }

  return (
    <div className="flex size-9 items-center justify-center rounded-full bg-muted">
      <MailIcon className="size-5 text-muted-foreground" />
    </div>
  );
}

function getDisplayName(user: NonNullable<ReturnType<typeof useAuthStore.getState>["user"]>): string {
  if (user.type === "whop" && user.whop_username) return user.whop_username;
  if (user.type === "discord" && user.discord_username) return user.discord_username;
  return user.email ?? "User";
}

function getDisplayEmail(user: NonNullable<ReturnType<typeof useAuthStore.getState>["user"]>): string | null {
  if (user.type === "whop" && user.whop_email) return user.whop_email;
  if (user.type === "free" && user.email) return user.email;
  return null;
}

function ProfileMenu() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);

  if (!isAuthenticated || !user) return null;

  const displayName = getDisplayName(user);
  const displayEmail = getDisplayEmail(user);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Profile menu"
        >
          <UserAvatar />
          <span className="absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full border-2 border-background bg-muted">
            <ChevronDownIcon className="size-3 text-muted-foreground" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="font-medium leading-none">{displayName}</p>
            {displayEmail && (
              <p className="text-xs text-muted-foreground leading-none">
                {displayEmail}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Discord link: shown to paid users (whop) who haven't linked
            Discord yet. Hidden for free users — they don't have Lab access,
            so there's no Discord server to join. */}
        {!user.discord_id && !isFreeUser(user) && (
          <>
            <DropdownMenuItem asChild>
              <a
                href="https://whop.com/joined/irreplaceable-engineer-lab/discord-Odzw6laOGxpUxL/app/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircleIcon className="size-4" />
                Join Discord
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={handleLogout}>
          <LogOutIcon className="size-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/dashboard"
            aria-label="Go to dashboard"
            className="flex items-center gap-2 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img src="/icon.svg" alt="" className="h-7 w-7" />
            <span className="font-sans text-lg font-semibold tracking-tight text-foreground">
              IE Lab
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <JoinLabButton />
            <ProfileMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        {children}
      </main>
    </div>
  );
}
